import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Building2, Briefcase, Plus, Target, Trash2, UserPlus, Users } from 'lucide-react';
import {
  adminEmpresaService,
  adminUserService,
  areaService,
  cargoService,
  catalogService,
  regraAtribuicaoService,
  vinculoService,
} from '../../shared/services';

type EmpresaOpcao = { id: string; nome: string };
type UsuarioOpcao = { id: string; nome: string; email: string };
type TrilhaOpcao = { id: string; nome: string };

function erroDe(error: unknown, padrao: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    if (data?.message) return data.message;
  }
  return padrao;
}

export function AdminEstruturaPage() {
  const queryClient = useQueryClient();
  const [empresaId, setEmpresaId] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const [novaArea, setNovaArea] = useState({ nome: '', codigo: '', parentId: '' });
  const [novoCargo, setNovoCargo] = useState({ nome: '', codigo: '', nivel: '' });
  const [novoVinculo, setNovoVinculo] = useState({ usuarioId: '', areaId: '', cargoId: '', gestorId: '', matricula: '' });
  const [novaRegra, setNovaRegra] = useState({ trilhaId: '', areaId: '', cargoId: '', prazoDias: '', obrigatoria: true });

  const { data: empresasData } = useQuery({
    queryKey: ['empresas-estrutura'],
    queryFn: () => adminEmpresaService.getEmpresas(),
  });

  const empresas = (Array.isArray(empresasData) ? empresasData : []) as EmpresaOpcao[];

  useEffect(() => {
    if (!empresaId && empresas.length > 0) {
      setEmpresaId(empresas[0].id);
    }
  }, [empresas, empresaId]);

  const { data: areas = [] } = useQuery({
    queryKey: ['areas', empresaId],
    queryFn: () => areaService.listar(empresaId),
    enabled: !!empresaId,
  });

  const { data: cargos = [] } = useQuery({
    queryKey: ['cargos', empresaId],
    queryFn: () => cargoService.listar(empresaId),
    enabled: !!empresaId,
  });

  const { data: usuariosData } = useQuery({
    queryKey: ['usuarios-estrutura'],
    queryFn: () => adminUserService.getUsers(),
  });

  const usuariosUsables = (Array.isArray(usuariosData) ? usuariosData : []).map((item: Record<string, unknown>) => ({
    id: String(item.id ?? item.Id ?? ''),
    nome: String(item.name ?? item.nome ?? item.Name ?? 'Sem nome'),
    email: String(item.email ?? item.Email ?? ''),
  })) as UsuarioOpcao[];

  const { data: vinculos = [] } = useQuery({
    queryKey: ['vinculos', empresaId],
    queryFn: () => vinculoService.listar({ empresaId }),
    enabled: !!empresaId,
  });

  const { data: trilhasData } = useQuery({
    queryKey: ['trilhas-regras'],
    queryFn: () => catalogService.getTrilhas(),
  });

  const trilhas = (Array.isArray(trilhasData) ? trilhasData : []) as TrilhaOpcao[];

  const { data: regras = [] } = useQuery({
    queryKey: ['regras-atribuicao', empresaId],
    queryFn: () => regraAtribuicaoService.listar(empresaId),
    enabled: !!empresaId,
  });

  const limparAvisos = () => {
    setErro(null);
    setAviso(null);
  };

  const criarArea = useMutation({
    mutationFn: () =>
      areaService.criar({
        empresaId,
        nome: novaArea.nome,
        codigo: novaArea.codigo || undefined,
        parentId: novaArea.parentId || null,
      }),
    onSuccess: () => {
      setNovaArea({ nome: '', codigo: '', parentId: '' });
      setAviso('Área criada.');
      setErro(null);
      queryClient.invalidateQueries({ queryKey: ['areas', empresaId] });
    },
    onError: (error) => setErro(erroDe(error, 'Não foi possível criar a área.')),
  });

  const removerArea = useMutation({
    mutationFn: (id: string) => areaService.remover(id),
    onSuccess: () => {
      setAviso('Área removida.');
      setErro(null);
      queryClient.invalidateQueries({ queryKey: ['areas', empresaId] });
    },
    onError: (error) => setErro(erroDe(error, 'Não foi possível remover a área.')),
  });

  const criarCargo = useMutation({
    mutationFn: () =>
      cargoService.criar({
        empresaId,
        nome: novoCargo.nome,
        codigo: novoCargo.codigo || undefined,
        nivel: novoCargo.nivel || undefined,
      }),
    onSuccess: () => {
      setNovoCargo({ nome: '', codigo: '', nivel: '' });
      setAviso('Cargo criado.');
      setErro(null);
      queryClient.invalidateQueries({ queryKey: ['cargos', empresaId] });
    },
    onError: (error) => setErro(erroDe(error, 'Não foi possível criar o cargo.')),
  });

  const removerCargo = useMutation({
    mutationFn: (id: string) => cargoService.remover(id),
    onSuccess: () => {
      setAviso('Cargo removido.');
      setErro(null);
      queryClient.invalidateQueries({ queryKey: ['cargos', empresaId] });
    },
    onError: (error) => setErro(erroDe(error, 'Não foi possível remover o cargo.')),
  });

  const salvarVinculo = useMutation({
    mutationFn: () =>
      vinculoService.salvar({
        empresaId,
        usuarioId: novoVinculo.usuarioId,
        areaId: novoVinculo.areaId || null,
        cargoId: novoVinculo.cargoId || null,
        gestorId: novoVinculo.gestorId || null,
        matricula: novoVinculo.matricula || undefined,
      }),
    onSuccess: () => {
      setNovoVinculo({ usuarioId: '', areaId: '', cargoId: '', gestorId: '', matricula: '' });
      setAviso('Vínculo atualizado.');
      setErro(null);
      queryClient.invalidateQueries({ queryKey: ['vinculos', empresaId] });
      queryClient.invalidateQueries({ queryKey: ['areas', empresaId] });
      queryClient.invalidateQueries({ queryKey: ['cargos', empresaId] });
    },
    onError: (error) => setErro(erroDe(error, 'Não foi possível salvar o vínculo.')),
  });

  const encerrarVinculo = useMutation({
    mutationFn: (id: string) => vinculoService.encerrar(id),
    onSuccess: () => {
      setAviso('Vínculo encerrado.');
      setErro(null);
      queryClient.invalidateQueries({ queryKey: ['vinculos', empresaId] });
    },
    onError: (error) => setErro(erroDe(error, 'Não foi possível encerrar o vínculo.')),
  });

  const criarRegra = useMutation({
    mutationFn: () =>
      regraAtribuicaoService.criar({
        empresaId,
        trilhaId: novaRegra.trilhaId,
        areaId: novaRegra.areaId || null,
        cargoId: novaRegra.cargoId || null,
        obrigatoria: novaRegra.obrigatoria,
        prazoDias: novaRegra.prazoDias ? Number(novaRegra.prazoDias) : null,
      }),
    onSuccess: () => {
      setNovaRegra({ trilhaId: '', areaId: '', cargoId: '', prazoDias: '', obrigatoria: true });
      setAviso('Regra criada e atribuições aplicadas.');
      setErro(null);
      queryClient.invalidateQueries({ queryKey: ['regras-atribuicao', empresaId] });
      queryClient.invalidateQueries({ queryKey: ['minhas-trilhas'] });
    },
    onError: (error) => setErro(erroDe(error, 'Não foi possível criar a regra.')),
  });

  const removerRegra = useMutation({
    mutationFn: (id: string) => regraAtribuicaoService.remover(id),
    onSuccess: () => {
      setAviso('Regra desativada. As atribuições já existentes foram mantidas.');
      setErro(null);
      queryClient.invalidateQueries({ queryKey: ['regras-atribuicao', empresaId] });
    },
    onError: (error) => setErro(erroDe(error, 'Não foi possível desativar a regra.')),
  });

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Estrutura por área</h1>
            <p className="text-gray-600 mt-1">
              Organize áreas, cargos e vínculos. A partir daqui o perfil de cada colaborador é montado.
            </p>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">Empresa</span>
            <select
              value={empresaId}
              onChange={(event) => setEmpresaId(event.target.value)}
              className="mt-1 block w-64 border border-gray-300 rounded-lg px-3 py-2"
            >
              {empresas.map((empresa) => (
                <option key={empresa.id} value={empresa.id}>
                  {empresa.nome}
                </option>
              ))}
            </select>
          </label>
        </div>

        {erro && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{erro}</div>}
        {aviso && <div className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">{aviso}</div>}

        {!empresaId ? (
          <div className="card text-center py-10 text-gray-600">Nenhuma empresa disponível para o seu perfil.</div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <section className="card">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                  <Building2 size={18} /> Áreas
                </h2>

                <form
                  className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4"
                  onSubmit={(event) => {
                    event.preventDefault();
                    limparAvisos();
                    criarArea.mutate();
                  }}
                >
                  <input
                    value={novaArea.nome}
                    onChange={(event) => setNovaArea({ ...novaArea, nome: event.target.value })}
                    placeholder="Nome da área"
                    className="border border-gray-300 rounded-lg px-3 py-2 sm:col-span-2"
                    required
                  />
                  <input
                    value={novaArea.codigo}
                    onChange={(event) => setNovaArea({ ...novaArea, codigo: event.target.value })}
                    placeholder="Código"
                    className="border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <select
                    value={novaArea.parentId}
                    onChange={(event) => setNovaArea({ ...novaArea, parentId: event.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2 sm:col-span-2"
                  >
                    <option value="">Sem área pai</option>
                    {areas.map((area) => (
                      <option key={area.id} value={area.id}>
                        {area.nome}
                      </option>
                    ))}
                  </select>
                  <button type="submit" disabled={criarArea.isPending} className="btn-primary flex items-center justify-center gap-1 disabled:opacity-60">
                    <Plus size={16} /> Criar
                  </button>
                </form>

                <ul className="divide-y divide-gray-100">
                  {areas.length === 0 && <li className="py-4 text-sm text-gray-500">Nenhuma área cadastrada.</li>}
                  {areas.map((area) => (
                    <li key={area.id} className="py-3 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {area.nome} {area.codigo && <span className="text-xs text-gray-500">({area.codigo})</span>}
                        </p>
                        <p className="text-xs text-gray-500">
                          {area.usuarios} colaborador(es){area.parentNome ? ` · subárea de ${area.parentNome}` : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Remover a área ${area.nome}?`)) {
                            limparAvisos();
                            removerArea.mutate(area.id);
                          }
                        }}
                        className="text-gray-400 hover:text-red-600"
                        title="Remover área"
                      >
                        <Trash2 size={16} />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="card">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                  <Briefcase size={18} /> Cargos
                </h2>

                <form
                  className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4"
                  onSubmit={(event) => {
                    event.preventDefault();
                    limparAvisos();
                    criarCargo.mutate();
                  }}
                >
                  <input
                    value={novoCargo.nome}
                    onChange={(event) => setNovoCargo({ ...novoCargo, nome: event.target.value })}
                    placeholder="Nome do cargo"
                    className="border border-gray-300 rounded-lg px-3 py-2 sm:col-span-2"
                    required
                  />
                  <input
                    value={novoCargo.codigo}
                    onChange={(event) => setNovoCargo({ ...novoCargo, codigo: event.target.value })}
                    placeholder="Código"
                    className="border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <input
                    value={novoCargo.nivel}
                    onChange={(event) => setNovoCargo({ ...novoCargo, nivel: event.target.value })}
                    placeholder="Nível (júnior, pleno...)"
                    className="border border-gray-300 rounded-lg px-3 py-2 sm:col-span-2"
                  />
                  <button type="submit" disabled={criarCargo.isPending} className="btn-primary flex items-center justify-center gap-1 disabled:opacity-60">
                    <Plus size={16} /> Criar
                  </button>
                </form>

                <ul className="divide-y divide-gray-100">
                  {cargos.length === 0 && <li className="py-4 text-sm text-gray-500">Nenhum cargo cadastrado.</li>}
                  {cargos.map((cargo) => (
                    <li key={cargo.id} className="py-3 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {cargo.nome} {cargo.codigo && <span className="text-xs text-gray-500">({cargo.codigo})</span>}
                        </p>
                        <p className="text-xs text-gray-500">
                          {cargo.usuarios} colaborador(es){cargo.nivel ? ` · ${cargo.nivel}` : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Remover o cargo ${cargo.nome}?`)) {
                            limparAvisos();
                            removerCargo.mutate(cargo.id);
                          }
                        }}
                        className="text-gray-400 hover:text-red-600"
                        title="Remover cargo"
                      >
                        <Trash2 size={16} />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            </div>

            <section className="card">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <Users size={18} /> Colaboradores
              </h2>

              <form
                className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-6"
                onSubmit={(event) => {
                  event.preventDefault();
                  limparAvisos();
                  salvarVinculo.mutate();
                }}
              >
                <select
                  value={novoVinculo.usuarioId}
                  onChange={(event) => setNovoVinculo({ ...novoVinculo, usuarioId: event.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 md:col-span-2"
                  required
                >
                  <option value="">Selecione o colaborador</option>
                  {usuariosUsables.map((usuario) => (
                    <option key={usuario.id} value={usuario.id}>
                      {usuario.nome} — {usuario.email}
                    </option>
                  ))}
                </select>

                <select
                  value={novoVinculo.areaId}
                  onChange={(event) => setNovoVinculo({ ...novoVinculo, areaId: event.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Área</option>
                  {areas.map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.nome}
                    </option>
                  ))}
                </select>

                <select
                  value={novoVinculo.cargoId}
                  onChange={(event) => setNovoVinculo({ ...novoVinculo, cargoId: event.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Cargo</option>
                  {cargos.map((cargo) => (
                    <option key={cargo.id} value={cargo.id}>
                      {cargo.nome}
                    </option>
                  ))}
                </select>

                <button type="submit" disabled={salvarVinculo.isPending} className="btn-primary flex items-center justify-center gap-1 disabled:opacity-60">
                  <UserPlus size={16} /> Vincular
                </button>

                <select
                  value={novoVinculo.gestorId}
                  onChange={(event) => setNovoVinculo({ ...novoVinculo, gestorId: event.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 md:col-span-2"
                >
                  <option value="">Sem gestor definido</option>
                  {usuariosUsables.map((usuario) => (
                    <option key={usuario.id} value={usuario.id}>
                      Gestor: {usuario.nome}
                    </option>
                  ))}
                </select>

                <input
                  value={novoVinculo.matricula}
                  onChange={(event) => setNovoVinculo({ ...novoVinculo, matricula: event.target.value })}
                  placeholder="Matrícula (opcional)"
                  className="border border-gray-300 rounded-lg px-3 py-2 md:col-span-3"
                />
              </form>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-4">Colaborador</th>
                      <th className="py-2 pr-4">Área</th>
                      <th className="py-2 pr-4">Cargo</th>
                      <th className="py-2 pr-4">Gestor</th>
                      <th className="py-2 pr-4">Início</th>
                      <th className="py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {vinculos.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-4 text-gray-500">
                          Nenhum vínculo ativo nesta empresa.
                        </td>
                      </tr>
                    )}
                    {vinculos.map((vinculo) => (
                      <tr key={vinculo.id} className="border-b border-gray-100">
                        <td className="py-2 pr-4">
                          <p className="font-medium text-gray-900">{vinculo.usuarioNome}</p>
                          <p className="text-xs text-gray-500">{vinculo.usuarioEmail}</p>
                        </td>
                        <td className="py-2 pr-4">{vinculo.areaNome ?? '—'}</td>
                        <td className="py-2 pr-4">{vinculo.cargoNome ?? '—'}</td>
                        <td className="py-2 pr-4">{vinculo.gestorNome ?? '—'}</td>
                        <td className="py-2 pr-4">{new Date(vinculo.inicio).toLocaleDateString('pt-BR')}</td>
                        <td className="py-2 text-right">
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(`Encerrar o vínculo de ${vinculo.usuarioNome}?`)) {
                                limparAvisos();
                                encerrarVinculo.mutate(vinculo.id);
                              }
                            }}
                            className="text-gray-400 hover:text-red-600"
                            title="Encerrar vínculo"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="card">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-2">
                <Target size={18} /> Regras de atribuição
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Toda pessoa com vínculo ativo que corresponder à área e ao cargo recebe a trilha automaticamente,
                com prazo opcional.
              </p>

              <form
                className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-6"
                onSubmit={(event) => {
                  event.preventDefault();
                  limparAvisos();
                  criarRegra.mutate();
                }}
              >
                <select
                  value={novaRegra.trilhaId}
                  onChange={(event) => setNovaRegra({ ...novaRegra, trilhaId: event.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 md:col-span-2"
                  required
                >
                  <option value="">Selecione a trilha</option>
                  {trilhas.map((trilha) => (
                    <option key={trilha.id} value={trilha.id}>
                      {trilha.nome}
                    </option>
                  ))}
                </select>

                <select
                  value={novaRegra.areaId}
                  onChange={(event) => setNovaRegra({ ...novaRegra, areaId: event.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Todas as áreas</option>
                  {areas.map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.nome}
                    </option>
                  ))}
                </select>

                <select
                  value={novaRegra.cargoId}
                  onChange={(event) => setNovaRegra({ ...novaRegra, cargoId: event.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Todos os cargos</option>
                  {cargos.map((cargo) => (
                    <option key={cargo.id} value={cargo.id}>
                      {cargo.nome}
                    </option>
                  ))}
                </select>

                <input
                  type="number"
                  min={0}
                  max={3650}
                  value={novaRegra.prazoDias}
                  onChange={(event) => setNovaRegra({ ...novaRegra, prazoDias: event.target.value })}
                  placeholder="Prazo (dias)"
                  className="border border-gray-300 rounded-lg px-3 py-2"
                />

                <label className="flex items-center gap-2 text-sm text-gray-700 px-2">
                  <input
                    type="checkbox"
                    checked={novaRegra.obrigatoria}
                    onChange={(event) => setNovaRegra({ ...novaRegra, obrigatoria: event.target.checked })}
                  />
                  Obrigatória
                </label>

                <button
                  type="submit"
                  disabled={criarRegra.isPending}
                  className="btn-primary flex items-center justify-center gap-1 md:col-span-6 disabled:opacity-60"
                >
                  <Plus size={16} /> {criarRegra.isPending ? 'Aplicando...' : 'Criar regra e atribuir'}
                </button>
              </form>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-4">Trilha</th>
                      <th className="py-2 pr-4">Área</th>
                      <th className="py-2 pr-4">Cargo</th>
                      <th className="py-2 pr-4">Prazo</th>
                      <th className="py-2 pr-4">Atribuições</th>
                      <th className="py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {regras.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-4 text-gray-500">
                          Nenhuma regra ativa nesta empresa.
                        </td>
                      </tr>
                    )}
                    {regras.map((regra) => (
                      <tr key={regra.id} className="border-b border-gray-100">
                        <td className="py-2 pr-4 font-medium text-gray-900">{regra.trilhaNome}</td>
                        <td className="py-2 pr-4">{regra.areaNome ?? 'Todas'}</td>
                        <td className="py-2 pr-4">{regra.cargoNome ?? 'Todos'}</td>
                        <td className="py-2 pr-4">
                          {regra.prazoDias ? `${regra.prazoDias} dias` : 'Sem prazo'}
                          {regra.obrigatoria ? ' · obrigatória' : ' · eletiva'}
                        </td>
                        <td className="py-2 pr-4">{regra.atribuicoes}</td>
                        <td className="py-2 text-right">
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(`Desativar a regra da trilha ${regra.trilhaNome}?`)) {
                                limparAvisos();
                                removerRegra.mutate(regra.id);
                              }
                            }}
                            className="text-gray-400 hover:text-red-600"
                            title="Desativar regra"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
